import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Input, notification, theme } from 'antd';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import { EditedData } from 'store/ducks/DataEntry/ICTServices/types';
import * as Styled from '../../DataEntry/DataEntry.styled';
import { RootState } from 'store/configureStore';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { globalOp } from 'store/ducks/Global';
import SaveButton from 'components/SaveButton/SaveButton';
import useFilters from 'utils/hooks/useFilters';
import { useTranslation } from 'react-i18next';

export interface IDataArea {
  id: number;
  key: number;
  data_point: string;
  description: string;
  int_options_def: string;
}

const Report99: React.FC = () => {
  const { query } = useFilters();
  const [data, setData] = useState<IDataArea[]>([]);
  const { t } = useTranslation();
  const [editedData, setEditedData] = useState<EditedData>({});
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const isLoggedIn = localStorage.getItem('authToken');
  const { selectedOption99 } = useSelector((state: RootState) => state.global);
  const { useToken } = theme;
  const { token } = useToken();

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof IDataArea,
    rowKey: string,
  ) => {
    const value = e.target.value;

    setData((prevData) => {
      const newData = prevData.map((item) =>
        item.key?.toString?.() === rowKey
          ? { ...item, [fieldName]: value, isEdited: true }
          : item,
      );

      setChangedData((prevChangedData) => {
        const existing = prevChangedData.find(
          (item) => item.key?.toString?.() === rowKey,
        );

        if (existing) {
          return prevChangedData.map((item) =>
            item.key?.toString?.() === rowKey
              ? { ...item, [fieldName]: value }
              : item,
          );
        } else {
          const newRow = newData.find(
            (item) => item.key?.toString?.() === rowKey,
          );
          if (newRow) {
            const key =
              newRow.key ??
              `generated-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            return [...prevChangedData, { ...newRow, [fieldName]: value, key }];
          }
        }

        return prevChangedData;
      });

      return newData;
    });
  };

  const updateChangedData = (editedData: any, changedData: any) => {
    if (editedData && typeof editedData === 'object') {
      Object.keys(editedData).forEach((key: string) => {
        const keyFromEditedData = parseInt(key.split('_')[0]);
        const matchingChangedRow = changedData.find((dataItem: any) => {
          return dataItem.ict_system_id === keyFromEditedData;
        });

        if (matchingChangedRow) {
          const fieldName = key.replace(/^\d+_/, '');
          if (matchingChangedRow.hasOwnProperty(fieldName)) {
            matchingChangedRow[fieldName] = editedData[key];
          }
        }
      });
    } else {
      console.log('editedData is not an object:', editedData);
    }

    setChangedData([...changedData]);
  };

  useEffect(() => {
    updateChangedData(editedData, changedData);
  }, [editedData]);

  const dataWithKey = data.map((item) => ({
    ...item,
    key: item.key ?? item.id ?? generateUniqueId(),
  }));

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '100'],
    position: ['topRight'] as ['topRight'],
  });

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const fetchReportDetails = async () => {
    try {
      const response = await globalOp.fetchReportDetails(
        selectedOption99,
        query.entityId,
      );
      const data = response?.data;

      const selectedData: any[] = data?.[selectedOption99] || [];

      const mappedData: IDataArea[] = selectedData.map((item: any) => ({
        ...item,
        key: item.id || generateUniqueId(),
      }));

      setData(mappedData);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setData([]);
    if (query.entityId && selectedOption99) {
      fetchReportDetails();
    }
  }, [query.entityId, selectedOption99]);

  const columns: EditableColumnType<IDataArea>[] = [
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('REPORT_99.DATA_POINT')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'data_point',
      editable: false,
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('REPORT_99.DESCRIPTION')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'description',
      editable: false,
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('REPORT_99.INTERNAL_OPTIONS_DEFINITION')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'int_options_def',
      editable: true,
      render: (text: string, record: IDataArea) => (
        <Input
          value={text ?? ''}
          onChange={(e) => {
            const key =
              record.key ??
              `generated-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            handleEditChange(e, 'int_options_def', key.toString());
          }}
        />
      ),
    },
  ];

  const handleSubmit = async () => {
    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'No new data has been changed',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const preparedData = data.map((item) => ({
      ...item,
      entity_id: query.entityId,
      id: item.id ?? generateUniqueId(),
    }));

    try {
      const response = await globalOp.updateReportDetails(
        selectedOption99,
        preparedData,
      );
      setChangedData([]);
      setData((prev) => prev.map((item) => ({ ...item, isEdited: false })));
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  return (
    <>
      <Styled.TooltipTableContainer>
        <Table
          dataSource={dataWithKey}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: pagination.showSizeChanger,
            pageSizeOptions: pagination.pageSizeOptions,
            position: pagination.position,
          }}
          columns={columns}
        />
      </Styled.TooltipTableContainer>
      {dataWithKey.length > 0 && (
        <SaveButton
          token={token}
          hasChanges={changedData.length > 0}
          onClick={handleSubmit}
        />
      )}
    </>
  );
};

export default Report99;
